/**
 * lite-youtube — A faster youtube embed.
 * Renders a poster + play button; only loads the real YouTube iframe on click.
 * Based on Paul Irish's <lite-youtube-embed> (Apache-2.0).
 *
 * Attributes:
 *   videoid="..."         — the YouTube video ID (required)
 *   playlabel="..."       — accessible play-button label
 *   params="rel=0&..."    — query string passed to the embed iframe
 *   nocookie              — use youtube-nocookie.com (privacy-enhanced) domain
 */
class LiteYTEmbed extends HTMLElement {
  connectedCallback() {
    this.videoId = this.getAttribute('videoid');

    let playBtnEl = this.querySelector('.lty-playbtn');
    this.playLabel =
      (playBtnEl && playBtnEl.textContent.trim()) ||
      this.getAttribute('playlabel') ||
      'Play video';

    if (!this.style.backgroundImage) {
      this.style.backgroundImage = `url("https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg")`;
    }

    if (!playBtnEl) {
      playBtnEl = document.createElement('button');
      playBtnEl.type = 'button';
      playBtnEl.classList.add('lty-playbtn');
      this.append(playBtnEl);
    }
    if (!playBtnEl.textContent) {
      const playBtnLabelEl = document.createElement('span');
      playBtnLabelEl.className = 'lyt-visually-hidden';
      playBtnLabelEl.textContent = this.playLabel;
      playBtnEl.append(playBtnLabelEl);
    }
    playBtnEl.removeAttribute('href');

    this.addEventListener('pointerover', LiteYTEmbed.warmConnections, { once: true });
    this.addEventListener('focusin', LiteYTEmbed.warmConnections, { once: true });

    this.addEventListener('click', this.activate);

    this.needsYTApi =
      this.hasAttribute('js-api') || this.querySelector('.lty-playbtn');
  }

  static addPrefetch(kind, url) {
    const linkEl = document.createElement('link');
    linkEl.rel = kind;
    linkEl.href = url;
    linkEl.crossOrigin = 'true';
    document.head.append(linkEl);
  }

  static warmConnections() {
    if (LiteYTEmbed.preconnected) return;
    LiteYTEmbed.addPrefetch('preconnect', 'https://www.youtube-nocookie.com');
    LiteYTEmbed.addPrefetch('preconnect', 'https://www.google.com');
    LiteYTEmbed.addPrefetch('preconnect', 'https://googleads.g.doubleclick.net');
    LiteYTEmbed.addPrefetch('preconnect', 'https://static.doubleclick.net');
    LiteYTEmbed.preconnected = true;
  }

  fetchYTPlayerApi() {
    if (window.YT || (window.YT && window.YT.Player)) return;
    this.ytApiPromise = new Promise((res, rej) => {
      const el = document.createElement('script');
      el.src = 'https://www.youtube.com/iframe_api';
      el.async = true;
      el.onload = () => {
        window.YT.ready(res);
      };
      el.onerror = rej;
      this.append(el);
    });
  }

  getParams() {
    const params = new URLSearchParams(this.getAttribute('params') || []);
    params.append('autoplay', '1');
    params.append('playsinline', '1');
    return params;
  }

  async activate() {
    if (this.classList.contains('lyt-activated')) return;

    const params = this.getParams();
    const domain = this.hasAttribute('nocookie') ? 'youtube-nocookie' : 'youtube';
    const iframeEl = document.createElement('iframe');
    iframeEl.width = 560;
    iframeEl.height = 315;
    iframeEl.title = this.playLabel;
    iframeEl.allow =
      'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    iframeEl.allowFullscreen = true;
    iframeEl.src = `https://www.${domain}.com/embed/${encodeURIComponent(
      this.videoId
    )}?${params.toString()}`;
    this.append(iframeEl);
    this.classList.add('lyt-activated');

    iframeEl.focus();
  }
}

if (!customElements.get('lite-youtube')) {
  customElements.define('lite-youtube', LiteYTEmbed);
}
